import React, { useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import List from '@sv-support-sdk/ui-components/src/components/Lists/List';
import Headline from '@sv-support-sdk/ui-components/src/components/Typography/Headline';

import PageContentInner from '../../components/Layout/Page/PageContentInner';
import Breadcrumbs from '../../components/Navigation/Breadcrumbs';
import Error from '../../components/Feedback/Error';
import Badge from '../../components/Feedback/Badge';
import ChatRate from '../../components/Chat/ChatRate';
import Conversation from './components/Conversation';
import Loader from '../../components/Feedback/Loader';

import { $l } from '../../locales';
import { routes } from '../../routing';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import chatsActions from '../../store/actions/chats';
import { selectUserLoaded, userAuthorizedSelector } from '../../store/selectors/user';
import { WORKFLOW_FRONT_URL } from '../../config';
import { selectChat, selectSelectedChatLoading } from '../../store/selectors/chats';

import css from './style.module.scss';

const Chat = () => {
  const { chatId } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isAuthorized = useAppSelector(userAuthorizedSelector);
  const isUserLoaded = useAppSelector(selectUserLoaded);
  const chat = useAppSelector(selectChat);
  const chatLoading = useAppSelector(selectSelectedChatLoading);

  useEffect(() => {
    return () => {
      dispatch(chatsActions.resetState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (chatId && isAuthorized) {
      dispatch(
        chatsActions.getOneChat(chatId, {
          notyTitle: $l('ALERTS.WARNING_TITLE'),
          notyMessage: $l('ALERTS.SOMETHING_WENT_WRONG'),
        }),
      );
    }
  }, [chatId, dispatch, isAuthorized]);

  useEffect(() => {
    if (isUserLoaded && !isAuthorized) {
      window.location.href = `${WORKFLOW_FRONT_URL}/login?redirect=${window.location.href}`;
    }
  }, [isUserLoaded, isAuthorized]);

  const breadcrumbs = useMemo(
    () => [
      { text: $l('BREADCRUMBS.SUPPORT_HISTORY'), link: routes.tickets },
      { text: `${$l('BREADCRUMBS.CHAT')} ${chatId}`, isLast: true },
    ],
    [chatId],
  );

  if (chatLoading && !chat) {
    return (
      <PageContentInner>
        <Breadcrumbs items={breadcrumbs} className="indent-md-bottom" />
        <div className={css.loader}>
          <Loader />
        </div>
      </PageContentInner>
    );
  }

  if (!chat && isAuthorized) {
    return (
      <PageContentInner>
        <Error
          title={$l('ERROR_PAGE.SOMETHING_WENT_WRONG')}
          description={$l('ERROR_PAGE.PROCESSING_PROBLEM')}
          icon="150--error-sign"
          action={{ text: $l('ERROR_PAGE.GO_TO_HOME'), onClick: () => navigate('/') }}
        />
      </PageContentInner>
    );
  }

  if (!chat) return null;

  return (
    <PageContentInner type="flex" direction="column">
      <Breadcrumbs items={breadcrumbs} className="indent-md-bottom" />
      <List gap="16" direction="row" isWrapped align="center">
        <Headline>
          {$l('CHAT.CHAT')} {chatId}
        </Headline>
        <Badge color="grey">
          <ChatRate iconSize="sm" rate={chat.score || chat.rate} />
        </Badge>
      </List>
      <div className={css.chat__header__info}>{chat.createdAt}</div>
      <Conversation rate={chat.score || chat.rate} messages={chat.messages} />
    </PageContentInner>
  );
};

export default Chat;
